<template>
  <div class="command-payload-form">
    <div
      v-if="command && command.description"
      class="command-payload-form__description mb-3"
    >
      {{ command.description }}
    </div>

    <wifi-connect-payload
      v-if="command && command.type === 'ConnectWifi'"
      :key="groupId"
      :command="command"
      :group-id="groupId"
      :company-id="wifiCompanyId"
      :disabled="disabled"
      :show-errors="showErrors"
      @input="$emit('input', $event)"
      @validity="$emit('validity', $event)"
    />
    <div
      v-else-if="!fields.length"
      class="command-payload-form__empty"
    >
      This command takes no payload.
    </div>

    <v-row
      v-else
      dense
    >
      <v-col
        v-for="field in fields"
        :key="field.key"
        cols="12"
        :md="field.input === 'textarea' || field.input === 'json' ? 12 : 6"
      >
        <!-- Choice fields, options loaded from the catalog's optionSources -->
        <v-autocomplete
          v-if="isChoice(field)"
          :value="valueOf(field)"
          :items="optionsFor(field)"
          :item-text="itemTextFor(field)"
          :item-value="itemValueFor(field)"
          :label="labelFor(field)"
          :loading="isLoadingOptions(field)"
          :disabled="disabled || isBlockedByDependency(field)"
          :error-messages="errorFor(field)"
          :hint="choiceHintFor(field)"
          :persistent-hint="!!choiceHintFor(field)"
          :no-data-text="noDataTextFor(field)"
          clearable
          @change="setValue(field, $event)"
        />

        <v-switch
          v-else-if="isBoolean(field)"
          :input-value="valueOf(field)"
          :label="labelFor(field)"
          :disabled="disabled"
          :hint="hintFor(field)"
          :persistent-hint="!!hintFor(field)"
          class="mt-2"
          @change="setValue(field, $event === true)"
        />

        <v-menu
          v-else-if="field.input === 'date'"
          :close-on-content-click="false"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="valueOf(field)"
              :label="labelFor(field)"
              :error-messages="errorFor(field)"
              :hint="hintFor(field)"
              :persistent-hint="!!hintFor(field)"
              :disabled="disabled"
              readonly
              clearable
              v-bind="attrs"
              v-on="on"
              @click:clear="setValue(field, null)"
            />
          </template>
          <v-date-picker
            :value="valueOf(field)"
            @input="setValue(field, $event)"
          />
        </v-menu>

        <v-textarea
          v-else-if="field.input === 'textarea' || field.input === 'json'"
          :value="valueOf(field)"
          :label="labelFor(field)"
          :placeholder="field.placeholder || (field.input === 'json' ? '{ }' : '')"
          :error-messages="errorFor(field)"
          :hint="hintFor(field)"
          :persistent-hint="!!hintFor(field)"
          :disabled="disabled"
          rows="4"
          @input="setValue(field, $event)"
        />

        <v-text-field
          v-else
          :value="valueOf(field)"
          :label="labelFor(field)"
          :type="field.input === 'number' ? 'number' : 'text'"
          :placeholder="field.placeholder || ''"
          :error-messages="errorFor(field)"
          :hint="hintFor(field)"
          :persistent-hint="!!hintFor(field)"
          :disabled="disabled"
          @input="setValue(field, field.input === 'number' ? toNumber($event) : $event)"
        />

        <v-alert
          v-if="isChoice(field) && errorInfoFor(field)"
          dense
          text
          type="error"
          class="mt-2 mb-0 command-payload-form__note"
        >
          <div>
            Could not load {{ field.label }} options<span
              v-if="errorInfoFor(field).status"
            > ({{ errorInfoFor(field).status }})</span>:
            {{ errorInfoFor(field).message }}
          </div>
          <div class="command-payload-form__path">
            GET {{ errorInfoFor(field).path }}
          </div>
          <v-btn
            x-small
            text
            color="error"
            class="mt-1 px-1"
            @click="retryOptions(field)"
          >
            Retry
          </v-btn>
        </v-alert>

        <v-alert
          v-else-if="isChoice(field) && emptyIntersectionFor(field)"
          dense
          text
          type="warning"
          class="mt-2 mb-0 command-payload-form__note"
        >
          {{ emptyIntersectionFor(field) }}
          Send the command per company, or split the group.
        </v-alert>

        <v-alert
          v-else-if="isChoice(field) && !urlsFor(field) && field.optionsSource && !isBlockedByDependency(field)"
          dense
          text
          type="warning"
          class="mt-2 mb-0 command-payload-form__note"
        >
          No company context is available to load {{ field.label }} options.
        </v-alert>
      </v-col>
    </v-row>
  </div>
</template>

<script>
  import WifiConnectPayload from './WifiConnectPayload.vue'
  import DeviceGroupsDataService from '@/services/DeviceGroupsDataService'
  import {
    editableFields,
    intersectOptionLists,
    resolveOptionsUrl,
    toRelativeApiPath,
  } from '@/util/commandCatalog'

  const CHOICE_INPUTS = ['select', 'autocomplete', 'combobox']
  const BOOLEAN_INPUTS = ['switch', 'boolean', 'checkbox', 'bool']

  export default {
    name: 'CommandPayloadForm',
    components: { WifiConnectPayload },

    props: {
      wifiCompanyId: { type: [Number, String], default: null },
      groupId: { type: [Number, String], default: null },
      // The payload object being built.
      value: {
        type: Object,
        default: () => ({}),
      },
      // A normalized command descriptor from the catalog.
      command: {
        type: Object,
        default: null,
      },
      // Catalog optionSources, keyed by name.
      optionSources: {
        type: Object,
        default: () => ({}),
      },
      companyId: {
        type: [Number, String],
        default: null,
      },
      // Every company represented in the target group. When it spans more than
      // one, per-company option lists are intersected.
      companyIds: {
        type: Array,
        default: () => [],
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      // Set true after a failed submit so required fields go red.
      showErrors: {
        type: Boolean,
        default: false,
      },
    },

    data () {
      return {
        // Resolved url -> option array, so switching command types re-uses loads.
        optionsCache: {},
        optionsMeta: {},
        optionsErrors: {},
        loadingUrls: {},
      }
    },

    computed: {
      fields () {
        return editableFields(this.command)
      },

      targetCompanyIds () {
        const ids = (this.companyIds || [])
          .map(id => (id === null || id === undefined ? null : id))
          .filter(id => id !== null && id !== '')

        const unique = [...new Set(ids)]
        return unique.length ? unique : (this.companyId ? [this.companyId] : [])
      },

      missingRequired () {
        return this.fields.filter(field => {
          if (!field.required) {
            return false
          }
          const value = this.valueOf(field)
          return value === null || value === undefined || value === ''
        })
      },

      isValid () {
        return this.missingRequired.length === 0
      },
    },

    watch: {
      command: {
        immediate: true,
        handler () {
          this.applyDefaults()
          this.$nextTick(() => this.loadAllOptions())
        },
      },

      isValid: {
        immediate: true,
        handler (valid) {
          if (this.command?.type !== 'ConnectWifi') this.$emit('validity', valid)
        },
      },
    },

    methods: {
      isChoice (field) {
        return CHOICE_INPUTS.includes(field.input) || !!field.optionsSource
      },

      isBoolean (field) {
        return BOOLEAN_INPUTS.includes(field.input)
      },

      labelFor (field) {
        return field.required ? `${field.label} *` : field.label
      },

      hintFor (field) {
        return field.help || ''
      },

      // Choice fields also report how the multi-company intersection narrowed
      // the list, so a short dropdown does not look like missing data.
      choiceHintFor (field) {
        const common = this.commonHintFor(field)
        const help = this.hintFor(field)

        if (common && help) {
          return `${help} — ${common}`
        }
        return common || help
      },

      errorFor (field) {
        if (!this.showErrors || !field.required) {
          return []
        }
        const value = this.valueOf(field)
        return value === null || value === undefined || value === ''
          ? [`${field.label} is required`]
          : []
      },

      valueOf (field) {
        const value = this.value ? this.value[field.key] : undefined
        return value === undefined ? null : value
      },

      setValue (field, next) {
        const payload = { ...(this.value || {}), [field.key]: next }

        // A parent choice changing invalidates anything derived from it.
        this.fields
          .filter(other => other.dependsOn === field.key)
          .forEach(other => {
            payload[other.key] = null
          })

        this.$emit('input', payload)
        this.$nextTick(() => this.loadAllOptions())
      },

      toNumber (raw) {
        if (raw === '' || raw === null || raw === undefined) {
          return null
        }
        const parsed = Number(raw)
        return Number.isNaN(parsed) ? null : parsed
      },

      applyDefaults () {
        if (!this.fields.length) {
          this.$emit('input', {})
          return
        }

        const payload = {}
        this.fields.forEach(field => {
          const existing = this.value ? this.value[field.key] : undefined
          if (existing !== undefined) {
            payload[field.key] = existing
          } else {
            payload[field.key] = field.default === undefined ? null : field.default
          }
        })

        this.$emit('input', payload)
      },

      sourceFor (field) {
        return field.optionsSource ? this.optionSources[field.optionsSource] : null
      },

      itemTextFor (field) {
        const source = this.sourceFor(field)
        return field.itemText || (source && source.itemText) || 'text'
      },

      itemValueFor (field) {
        const source = this.sourceFor(field)
        return field.itemValue || (source && source.itemValue) || 'value'
      },

      // A dependent dropdown stays disabled until its parent has a value.
      isBlockedByDependency (field) {
        if (!field.dependsOn) {
          return false
        }
        const parent = this.value ? this.value[field.dependsOn] : null
        return parent === null || parent === undefined || parent === ''
      },

      // One url per company in the target group. Returns null when a
      // placeholder is still unresolved, i.e. nothing to load yet.
      urlsFor (field) {
        const source = this.sourceFor(field)
        if (!source || !source.url) {
          return null
        }

        const perCompany = /\{companyId\}/i.test(source.url)
        const ids = perCompany && this.targetCompanyIds.length
          ? this.targetCompanyIds
          : [this.companyId]

        const urls = ids.map(companyId => resolveOptionsUrl(source.url, {
          companyId,
          payload: this.value,
          field,
        }))

        return urls.every(Boolean) ? urls : null
      },

      cacheKeyFor (field) {
        const urls = this.urlsFor(field)
        return urls ? urls.join('|') : null
      },

      optionsFor (field) {
        const key = this.cacheKeyFor(field)
        return key ? (this.optionsCache[key] || []) : []
      },

      isLoadingOptions (field) {
        const key = this.cacheKeyFor(field)
        return !!(key && this.loadingUrls[key])
      },

      metaFor (field) {
        const key = this.cacheKeyFor(field)
        return (key && this.optionsMeta[key]) || null
      },

      // Explains why a dropdown is shorter than one company's own app list.
      commonHintFor (field) {
        const meta = this.metaFor(field)
        if (!meta || meta.companyCount < 2) {
          return ''
        }

        const widest = Math.max(...meta.listSizes)
        return meta.commonCount
          ? `${meta.commonCount} of ${widest} available in all ${meta.companyCount} companies in this group`
          : ''
      },

      emptyIntersectionFor (field) {
        const meta = this.metaFor(field)
        if (!meta || meta.companyCount < 2 || meta.commonCount) {
          return ''
        }
        return `No ${field.label.toLowerCase()} is common to all ${meta.companyCount} companies in this group.`
      },

      noDataTextFor (field) {
        if (this.isBlockedByDependency(field)) {
          const parent = this.fields.find(entry => entry.key === field.dependsOn)
          return `Select ${parent ? parent.label.toLowerCase() : 'the previous field'} first`
        }

        const meta = this.metaFor(field)
        if (meta && meta.companyCount > 1 && !meta.commonCount) {
          return `Nothing common to all ${meta.companyCount} companies`
        }

        return 'No options found'
      },

      loadAllOptions () {
        this.fields
          .filter(field => this.isChoice(field) && field.optionsSource)
          .forEach(field => this.loadOptions(field))
      },

      async fetchOptionList (url) {
        const path = toRelativeApiPath(url)
        const response = await DeviceGroupsDataService.getCommandOptions(path)
        const data = response.data

        // Accept a bare array or any of the usual envelope shapes.
        const items = Array.isArray(data)
          ? data
          : (data && (data.items || data.options || data.results || data.result || data.data)) || []

        return Array.isArray(items) ? items : []
      },

      describeError (error, url) {
        const status = error && error.response ? error.response.status : null
        const body = error && error.response ? error.response.data : null
        const message = (body && (body.message || body.title || body.error)) ||
          (error && error.message) ||
          'Request failed'

        return {
          status,
          message,
          path: toRelativeApiPath(url),
        }
      },

      errorInfoFor (field) {
        const key = this.cacheKeyFor(field)
        return (key && this.optionsErrors[key]) || null
      },

      retryOptions (field) {
        const key = this.cacheKeyFor(field)
        if (!key) {
          return
        }
        this.$delete(this.optionsCache, key)
        this.$delete(this.optionsErrors, key)
        this.loadOptions(field)
      },

      async loadOptions (field) {
        const urls = this.urlsFor(field)
        const key = urls ? urls.join('|') : null

        if (!key || this.optionsCache[key] || this.loadingUrls[key]) {
          return
        }

        this.$set(this.loadingUrls, key, true)
        this.$delete(this.optionsErrors, key)

        try {
          const lists = await Promise.all(urls.map(url => this.fetchOptionList(url)))
          const { options, meta } = intersectOptionLists(lists, this.itemValueFor(field))

          this.$set(this.optionsCache, key, options)
          this.$set(this.optionsMeta, key, meta)
        } catch (error) {
          console.error(
            `Failed to load options for "${field.optionsSource}" from ${urls.join(', ')}:`,
            error,
          )
          this.$set(this.optionsCache, key, [])
          this.$set(this.optionsMeta, key, null)
          this.$set(this.optionsErrors, key, this.describeError(error, urls[0]))
        } finally {
          this.$set(this.loadingUrls, key, false)
        }
      },
    },
  }
</script>

<style scoped>
.command-payload-form__description {
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.6);
}

.command-payload-form__note {
  font-size: 0.8rem;
}

.command-payload-form__path {
  font-family: monospace;
  font-size: 0.75rem;
  opacity: 0.85;
  word-break: break-all;
}

.command-payload-form__empty {
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.5);
  font-style: italic;
}
</style>
